import axios from 'axios';

// Use production URL if on production domain, otherwise use env var or localhost
const getApiBaseUrl = () => {
  // Explicit env var always wins (set this in Vercel for demo project)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (window.location.hostname === 'portal.jjtutorialsg.com') {
    return 'https://jjt-tutor-portal.onrender.com/api';
  }
  if (window.location.hostname === 'demo.jjtutorialsg.com') {
    return 'https://jjt-tutor-portal-backend-demo.onrender.com/api';
  }
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instances
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 second timeout for cold starts
});

const adminAPI = axios.create({
  baseURL: `${API_BASE_URL}/admin`,
  timeout: 60000,
});

const tutorAPI = axios.create({
  baseURL: `${API_BASE_URL}/tutor`,
  timeout: 60000,
});

// Request interceptor to attach JWT token
const requestInterceptor = (config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const requestErrorInterceptor = (error) => {
  return Promise.reject(error);
};

// Response interceptor for global error handling
const responseInterceptor = (response) => {
  return response;
};

const responseErrorInterceptor = (error) => {
  // Redirect to login on 401 errors
  if (error.response && error.response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  }
  return Promise.reject(error);
};

// Add interceptors to all axios instances
[api, adminAPI, tutorAPI].forEach(instance => {
  instance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
  instance.interceptors.response.use(responseInterceptor, responseErrorInterceptor);
});

// Add auth token to requests (legacy function, kept for compatibility)
const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    adminAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    tutorAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
    delete adminAPI.defaults.headers.common['Authorization'];
    delete tutorAPI.defaults.headers.common['Authorization'];
  }
};

// Admin API functions
export const adminApi = {
  // Auth
  login: (email, password, turnstileToken) => api.post('/auth/login', { email, password, turnstileToken }),
  changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { currentPassword, newPassword }),
  
  // Notification Preferences
  getNotificationPreferences: () => api.get('/users/preferences/notifications'),
  updateNotificationPreferences: (preferences) => api.put('/users/preferences/notifications', { notificationPreferences: preferences }),
  
  // Tutors
  createTutor: (tutorData) => {
    return adminAPI.post('/tutors', tutorData);
  },
  updateTutor: (id, tutorData) => {
    return adminAPI.put(`/tutors/${id}`, tutorData);
  },
  deleteTutor: (id) => adminAPI.delete(`/tutors/${id}`), // Soft delete (archive)
  permanentlyDeleteTutor: (id) => adminAPI.delete(`/tutors/${id}/permanent`), // Hard delete
  resetTutorPassword: (id) => adminAPI.post(`/tutors/${id}/reset-password`), // Reset password
  getTutors: () => adminAPI.get('/tutors'),
  getTutorById: (id) => adminAPI.get(`/tutors/${id}`),
  getTutorTutees: (id) => adminAPI.get(`/tutors/${id}/tutees`),
  uploadTutorProfileImage: (id, formData) => adminAPI.post(`/tutors/${id}/profile-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  removeTutorProfileImage: (id) => adminAPI.delete(`/tutors/${id}/profile-image`),
  
  // Bulk tutor operations
  bulkArchiveTutors: (tutorIds) => adminAPI.post('/tutors/bulk/archive', { tutorIds }),
  bulkUnarchiveTutors: (tutorIds) => adminAPI.post('/tutors/bulk/unarchive', { tutorIds }),
  bulkResetTutorPasswords: (tutorIds) => adminAPI.post('/tutors/bulk/reset-passwords', { tutorIds }),
  
  // Tutees (changed from students to tutees)
  createTutee: (tuteeData) => adminAPI.post('/tutees', tuteeData),
  updateTutee: (id, tuteeData) => adminAPI.put(`/tutees/${id}`, tuteeData),
  deleteTutee: (id) => adminAPI.delete(`/tutees/${id}`), // Soft delete (archive)
  permanentlyDeleteTutee: (id) => adminAPI.delete(`/tutees/${id}/permanent`), // Hard delete
  getTutees: () => adminAPI.get('/tutees'),
  getTuteeById: (id) => adminAPI.get(`/tutees/${id}`),
  
  // Bulk tutee operations
  bulkArchiveTutees: (tuteeIds) => adminAPI.post('/tutees/bulk/archive', { tuteeIds }),
  bulkUnarchiveTutees: (tuteeIds) => adminAPI.post('/tutees/bulk/unarchive', { tuteeIds }),
  bulkUpdateTuteeLevels: (tuteeIds, level, year) => adminAPI.post('/tutees/bulk/update-levels', { tuteeIds, level, year }),
  
  // Subject list management
  getSubjectList: () => adminAPI.get('/subject-list'),
  updateSubjectList: (subjects) => adminAPI.put('/subject-list', { subjects }),
  
  // Tutor rate settings management
  getTutorRateSettings: () => adminAPI.get('/tutor-rate-settings'),
  updateTutorRateSettings: (rates) => adminAPI.put('/tutor-rate-settings', { rates }),
  
  // Subject assignment
  assignTutorToSubject: (assignmentData) => adminAPI.post('/assign-tutor', assignmentData),
  removeTutorFromSubject: (assignmentData) => adminAPI.post('/remove-tutor-from-subject', assignmentData),

  // Lessons
  getMonthlyLessons: (month, year) => adminAPI.get(`/lessons/monthly?month=${month}&year=${year}`),
  getTuteeInvoice: (tuteeId, month, year) => adminAPI.get(`/lessons/invoice/${tuteeId}?month=${month}&year=${year}`),
  checkUnendorsedLessons: (tuteeId, subjectIndex, tutorId) => adminAPI.get(`/tutees/${tuteeId}/subjects/${subjectIndex}/tutor/${tutorId}/unendorsed-lessons`),
  bulkUpdateLessonRates: (lessonIds, newRate, tutorId, tuteeId, subject, rateType) => {
    return adminAPI.put('/lessons/bulk-update-rate', { lessonIds, newRate, tutorId, tuteeId, subject, rateType });
  },
  updateLesson: (lessonId, lessonData) => adminAPI.put(`/lessons/${lessonId}`, lessonData),
  deleteLesson: (lessonId) => adminAPI.delete(`/lessons/${lessonId}`),
  adminCreateLesson: (lessonData) => adminAPI.post('/lessons/admin-create', lessonData),

  // Families
  getFamilies: () => adminAPI.get('/families'),
  getFamilyById: (familyNumber) => adminAPI.get(`/families/${familyNumber}`),

  // Endorsements
  getEndorsementHistory: (params) => adminAPI.get(`/endorsements/history?${params}`),
  resolveDispute: (lessonId, resolution, updatedLessonData) => 
    adminAPI.put(`/endorsements/resolve-dispute/${lessonId}`, { resolution, updatedLessonData }),
  getEndorsementStatus: (month, year, signal) => 
    api.get(`/endorsements/status/${month}/${year}`, { signal }),
  getEndorsementStatistics: (month, year, signal) => 
    api.get(`/endorsements/statistics/${month}/${year}`, { signal }),
  getTutorEndorsementDetails: (tutorId, month, year) => 
    api.get(`/endorsements/tutor/${tutorId}/${month}/${year}`),
  getTutorYearlyTrend: (tutorId, year, signal) => 
    api.get(`/endorsements/tutor/${tutorId}/yearly/${year}`, { signal }),
  sendEndorsementReminder: (tutorId, month, year) => 
    api.post(`/endorsements/remind/${tutorId}/${month}/${year}`),
  markInvoiceAsPaid: (tutorId, month, year, notes = '') => 
    adminAPI.post(`/mark-invoice-paid/${tutorId}/${month}/${year}`, { notes }),
  revertToPending: (tutorId, month, year, reason) =>
    api.post(`/endorsements/revert/${tutorId}/${month}/${year}`, { reason }),
  refreshTutorInvoiceCache: (month, year) => 
    api.post('/endorsements/refresh-cache', { month, year }),

  // Invoices
  getTutorInvoice: (tutorId, month, year) => 
    api.get(`/invoices/tutor/${tutorId}/${month}/${year}`),
  getTuteeInvoiceData: (tuteeId, month, year) => 
    api.get(`/invoices/tutee/${tuteeId}/${month}/${year}`),
  exportInvoicePDF: (data) => 
    api.post('/invoices/export-pdf', data, { responseType: 'blob' }),
  bulkExportInvoices: (data) => 
    api.post('/invoices/bulk-export', data, { responseType: 'blob' }),
  emailInvoice: (data) => 
    api.post('/invoices/email', data),
  bulkEmailInvoices: (data) => 
    api.post('/invoices/bulk-email', data),

  // Invoice Audit Trail
  getInvoiceAuditTrail: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/invoices/audit?${queryString}`);
  },
  getInvoiceAuditSummary: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/invoices/audit/summary?${queryString}`);
  },

  // Tutee Invoice Audit Trail
  getTuteeInvoiceAuditTrail: (invoiceId, params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/tutee-invoices/${invoiceId}/audit?${queryString}`);
  },
  getAllTuteeInvoiceAuditLogs: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/tutee-invoices/audit?${queryString}`);
  },
  getDisputeAuditTrail: (disputeId, params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/invoice-disputes/${disputeId}/audit?${queryString}`);
  },
  getCommissionAdjustmentAuditTrail: (adjustmentId, params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/commission-adjustments/${adjustmentId}/audit?${queryString}`);
  },

  // Invoice Template Settings
  getTemplateSettings: () => 
    api.get('/admin/invoice-template'),
  updateTemplateSettings: (data) => 
    api.put('/admin/invoice-template', data),
  uploadTemplateLogo: (data) => 
    api.post('/admin/invoice-template/logo', data),
  removeTemplateLogo: () => 
    api.delete('/admin/invoice-template/logo'),
  getTemplateLogo: () =>
    api.get('/admin/invoice-template/logo'),

  // Profit Share Allocations
  getProfitShareAllocation: (month, year) => 
    api.get('/profit-share/allocation', { params: { month, year } }),
  saveProfitShareAllocation: (month, year, allocations) =>
    api.post('/profit-share/allocation', { month, year, allocations }),

  // Claims Management
  getClaims: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/claims?${queryString}`);
  },
  getClaimsStatistics: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/claims/statistics?${queryString}`);
  },
  createClaim: (claimData) => api.post('/claims', claimData),
  getClaimById: (id) => api.get(`/claims/${id}`),
  // approveClaim accepts an optional recovery selection to charge the cost to a parent's draft invoice
  approveClaim: (id, notes, recovery = null) =>
    api.put(`/claims/${id}/approve`, {
      notes,
      ...(recovery && recovery.recoverFromParent
        ? {
            recoverFromParent: true,
            recoveryTutee: recovery.recoveryTutee,
            recoveryMonth: recovery.recoveryMonth,
            recoveryYear: recovery.recoveryYear,
            recoveryLabel: recovery.recoveryLabel
          }
        : {})
    }),
  getClaimRecoveryOptions: () => api.get('/claims/recovery-options'),
  rejectClaim: (id, reason) => api.put(`/claims/${id}/reject`, { reason }),
  cancelClaim: (id, reason) => api.put(`/claims/${id}/cancel`, { reason }),
  deleteClaim: (id) => api.delete(`/claims/${id}`),
  processClaimToMonth: (id, targetMonth, targetYear) => api.post(`/claims/${id}/process`, { targetMonth, targetYear }),
  // Add cache-busting and force refresh
  getClaimTypes: () => {
    const timestamp = Date.now();
    return api.get(`/claims/types?v=${timestamp}&_=${Math.random()}`);
  },

  // Tutee Invoice Management
  getTuteeInvoices: (params, signal) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/tutee-invoices?${queryString}`, { signal });
  },
  getTuteeInvoiceById: (id) => api.get(`/tutee-invoices/${id}`),
  updateTuteeInvoice: (id, data) => api.put(`/tutee-invoices/${id}`, data),
  deleteTuteeInvoice: (id) => api.delete(`/tutee-invoices/${id}`),
  assignFinanceICToInvoice: (invoiceId, financeICId) => 
    api.post(`/tutee-invoices/${invoiceId}/assign-finance-ic`, { financeICId }),
  // Per-month override of the Payment IC (PayNow collector) on a single invoice
  assignPaymentICToInvoice: (invoiceId, paymentICId) =>
    api.post(`/tutee-invoices/${invoiceId}/assign-payment-ic`, { paymentICId }),
  markTuteeInvoiceAsPaid: (invoiceId, paymentData = {}) => {
    // Forward an optional `mode` ('verify' | 'paid'); the caller's mode always
    // wins and defaults to 'paid' when absent (back-compat). Destructure+reapply
    // so the result does not depend on spread order.
    const { mode, ...rest } = paymentData;
    return api.post(`/tutee-invoices/${invoiceId}/mark-paid`, { mode: mode || 'paid', ...rest });
  },
  downloadTuteeInvoicePDF: (invoiceId) =>
    api.get(`/tutee-invoices/${invoiceId}/download-pdf`, { responseType: 'blob' }),
  downloadAllAssignedInvoices: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/tutee-invoices/finance-ic/download-all?${queryString}`, { responseType: 'blob' });
  },
  // Dynamic workflow endpoints
  finalizeTuteeInvoice: (invoiceId) =>
    api.post(`/tutee-invoices/${invoiceId}/finalize`),
  markTuteeInvoiceAsSent: (invoiceId) =>
    api.post(`/tutee-invoices/${invoiceId}/mark-sent`),
  // Send a single invoice (family-grouped) to the parent via WhatsApp
  sendTuteeInvoiceWhatsApp: (invoiceId) =>
    api.post(`/tutee-invoices/${invoiceId}/send-whatsapp`),
  // Send all of the caller's finalized/sent invoices for a month via WhatsApp
  sendBulkTuteeInvoiceWhatsApp: (month, year) =>
    api.post(`/tutee-invoices/send-whatsapp-bulk`, { month, year }),
  // Send a payment reminder to all SENT + UNPAID invoices for a month (skips paymentIndicated)
  sendBulkPaymentReminders: (month, year) =>
    api.post(`/tutee-invoices/send-reminders-bulk`, { month, year }),

  // Payment IC monthly summary (admin only). paymentICId = PaymentIC record id.
  previewIcPaymentSummary: (month, year, paymentICId) =>
    api.get(`/tutee-invoices/ic-summary/preview?month=${month}&year=${year}&paymentICId=${paymentICId}`),
  sendIcPaymentSummary: (month, year, paymentICId) =>
    api.post('/tutee-invoices/ic-summary/send', { month, year, paymentICId }),

  // WhatsApp inbound messages inbox (admin only)
  getWhatsappMessages: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/whatsapp/messages${qs ? `?${qs}` : ''}`);
  },
  getWhatsappUnreadCount: () => api.get('/whatsapp/messages/unread-count'),
  markWhatsappMessageRead: (id) => api.put(`/whatsapp/messages/${id}/read`),
  markAllWhatsappMessagesRead: () => api.put('/whatsapp/messages/read-all'),
  // Chat: conversation list + full thread with a parent + send a reply
  getWhatsappConversations: () => api.get('/whatsapp/conversations'),
  getWhatsappThread: (phone) => api.get(`/whatsapp/thread/${encodeURIComponent(phone)}`),
  sendWhatsappReply: (phone, text) => api.post('/whatsapp/send', { phone, text }),
  reviseTuteeInvoice: (invoiceId, revisionReason) =>
    api.post(`/tutee-invoices/${invoiceId}/revise`, { revisionReason }),
  getDraftTuteeInvoices: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/tutee-invoices/draft?${queryString}`);
  },
  getEligibleFinanceICs: () => adminAPI.get('/finance-ic/eligible-tutors'),
  toggleFinanceICEligibility: (tutorId, data) => adminAPI.post(`/finance-ic/${tutorId}/toggle-eligibility`, data),
  getFinanceICAssignments: () => adminAPI.get('/finance-ic/assignments'),
  assignDefaultFinanceIC: (tuteeIds, financeICId, updateDraftInvoices = false) => 
    adminAPI.post('/finance-ic/assign-default', { tuteeIds, financeICId, updateDraftInvoices }),
  removeDefaultFinanceIC: (tuteeIds) => 
    adminAPI.post('/finance-ic/remove-default', { tuteeIds }),

  // Payment IC management (the PayNow collector, decoupled from Finance IC)
  getPaymentICs: (includeInactive = false) =>
    adminAPI.get(`/payment-ic${includeInactive ? '?includeInactive=true' : ''}`),
  createPaymentIC: (data) => adminAPI.post('/payment-ic', data),
  deletePaymentIC: (id) => adminAPI.delete(`/payment-ic/${id}`),
  assignDefaultPaymentIC: (tuteeIds, paymentICId, updateDraftInvoices = false) =>
    adminAPI.post('/payment-ic/assign-default', { tuteeIds, paymentICId, updateDraftInvoices }),
  getPaymentICAssignments: () => adminAPI.get('/payment-ic/assignments'),
  removeDefaultPaymentIC: (tuteeIds) =>
    adminAPI.post('/payment-ic/remove-default', { tuteeIds }),
  // Combined view: all tutees with both Finance IC + Payment IC, plus pick lists
  getIcAssignmentsOverview: () => adminAPI.get('/ic-assignments/overview'),
  getMonthlyConfirmationStatus: (month, year) => 
    api.get(`/tutee-invoices/monthly-confirmation/${month}/${year}/status`),
  confirmMonthlyInvoices: (month, year, tutorPaymentNotes = '') => 
    api.post(`/tutee-invoices/confirm-all`, { month, year, tutorPaymentNotes }),

  // Invoice Disputes
  submitInvoiceDispute: (disputeData) => api.post('/invoice-disputes', disputeData),
  getInvoiceDisputes: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/invoice-disputes?${queryString}`);
  },
  getDisputeById: (id) => api.get(`/invoice-disputes/${id}`),
  reviewDispute: (id, reviewData) => api.put(`/invoice-disputes/${id}/review`, reviewData),
  markDisputeUnderReview: (id) => api.put(`/invoice-disputes/${id}/mark-under-review`),
  getDisputeStatistics: () => api.get('/invoice-disputes/statistics'),

  // Invoice Regeneration
  previewInvoiceRegeneration: (invoiceId) => api.get(`/tutee-invoices/${invoiceId}/preview-regeneration`),
  regenerateInvoice: (invoiceId, data) => api.post(`/tutee-invoices/${invoiceId}/regenerate`, data),
  refreshAllDraftInvoices: (month, year) => api.post(`/tutee-invoices/refresh-drafts`, { month, year }),
  finalizeAllDraftInvoices: (month, year) => api.post(`/tutee-invoices/finalize-all`, { month, year }),
  reviseTuteeInvoice: (invoiceId, revisionReason) => api.post(`/tutee-invoices/${invoiceId}/revise`, { revisionReason }),

  // Tutee Invoice Adjustments
  addTuteeInvoiceAdjustment: (invoiceId, adjustmentData) =>
    api.post(`/tutee-invoices/${invoiceId}/adjustments`, adjustmentData),
  deleteTuteeInvoiceAdjustment: (invoiceId, adjustmentId) =>
    api.delete(`/tutee-invoices/${invoiceId}/adjustments/${adjustmentId}`),
  getTuteeInvoiceAdjustments: (invoiceId) =>
    api.get(`/tutee-invoices/${invoiceId}/adjustments`),
  previewTuteeInvoiceAdjustment: (amount, targetMonth, targetYear) =>
    api.post('/tutee-invoices/adjustments/preview', { amount, targetMonth, targetYear }),

  // Profit Sharer Adjustments
  getPendingProfitSharerAdjustments: () => api.get('/profit-sharer-adjustments/pending'),
  getProfitSharerAdjustments: (profitSharerId, status) => {
    const params = status ? `?status=${status}` : '';
    return api.get(`/profit-sharer-adjustments/profit-sharer/${profitSharerId}${params}`);
  },
  approveProfitSharerAdjustment: (adjustmentId, notes) => 
    api.post(`/profit-sharer-adjustments/${adjustmentId}/approve`, { notes }),
  rejectProfitSharerAdjustment: (adjustmentId, notes) => 
    api.post(`/profit-sharer-adjustments/${adjustmentId}/reject`, { notes }),
};

// Tutor API functions
export const tutorApi = {
  // Auth
  login: (email, password, turnstileToken) => api.post('/auth/login', { email, password, turnstileToken }),
  changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { currentPassword, newPassword }),
  
  // Notification Preferences
  getNotificationPreferences: () => api.get('/users/preferences/notifications'),
  updateNotificationPreferences: (preferences) => api.put('/users/preferences/notifications', { notificationPreferences: preferences }),
  
  // Tutees (changed from students to tutees)
  getTutees: () => tutorAPI.get('/tutees'),
  getTuteeDetails: (id) => tutorAPI.get(`/tutees/${id}`),
  updateTuteeSchedule: (tuteeId, subjectId, schedules) => 
    tutorAPI.put(`/tutees/${tuteeId}/schedule`, { subjectId, schedules }),
  
  // Lessons
  getLessons: (month, year) => tutorAPI.get(`/lessons?month=${month}&year=${year}`),
  submitLesson: (lessonData) => tutorAPI.post('/lessons', lessonData),
  
  // Profile
  getMyProfile: () => tutorAPI.get('/profile'),
  updateMyProfile: (profileData) => tutorAPI.put('/profile', profileData),
  uploadProfileImage: (formData) => tutorAPI.post('/profile/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  removeProfileImage: () => tutorAPI.delete('/profile/image'),
  
  // Endorsements
  getMonthlySummaries: () => api.get('/endorsements/summaries'),
  getMonthlySummary: (year, month) => api.get(`/endorsements/summary/${year}/${month}`),
  endorseMonthlySummary: (year, month, notes) => api.post(`/endorsements/endorse/${year}/${month}`, { notes }),
  disputeLesson: (lessonId, reason) => api.post(`/endorsements/dispute-lesson/${lessonId}`, { reason }),
  // Invoice endpoints for tutors
  getMyInvoice: (month, year) => tutorAPI.get(`/invoice/${month}/${year}`),
  exportMyInvoicePDF: (month, year) => 
    tutorAPI.post('/invoice/export-pdf', { month, year }, { responseType: 'blob' }),

  // Profit Sharing Management
  getDefaultProfitShareAllocations: () => adminAPI.get('/profit-share/default'),
  saveDefaultProfitShareAllocations: (allocations) => adminAPI.post('/profit-share/default', { allocations }),
  getProfitShareAllocation: (month, year) => adminAPI.get(`/profit-share/allocation/${month}/${year}`),
  saveProfitShareAllocation: (month, year, allocations) => 
    adminAPI.post(`/profit-share/allocation/${month}/${year}`, { allocations }),
  deleteProfitShareAllocation: (month, year) => adminAPI.delete(`/profit-share/allocation/${month}/${year}`),
};

// Export axios instances and auth function
export { api, adminAPI, tutorAPI, setAuthToken };